import { useEffect, useState, type FormEvent } from "react";
import { Loader2, Lock, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createTopic, fetchTemplates, type Template } from "@/lib/api";
import { DEPARTMENTS } from "@/lib/departments";
import { cn } from "@/lib/utils";

/**
 * Create a PLAIN discussion topic as the signed-in user. The privileged seed is never set here —
 * that stays leadership-only via the MCP "Register Meeting Topic" tool.
 */
export function NewTopicDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDepartment("");
    setSelected(new Set());
    setIsPrivate(false);
    setError(null);
    const ac = new AbortController();
    fetchTemplates(ac.signal).then(setTemplates).catch(() => {});
    return () => ac.abort();
  }, [open]);

  function toggle(slug: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      await createTopic({
        title: t,
        templates: [...selected],
        department: department || null,
        is_private: isPrivate,
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create topic");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>New topic</DialogTitle>
            <DialogDescription>
              Create a discussion topic for the team. Privileged seed context is set separately by
              leadership.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="topic-title" className="text-sm font-medium">
                Title
              </label>
              <input
                id="topic-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Q3 roadmap planning"
                autoFocus
                className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="topic-department" className="text-sm font-medium">
                Department <span className="font-normal text-muted">(optional)</span>
              </label>
              <select
                id="topic-department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600"
              >
                <option value="">— None —</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-surface px-3 py-2.5 text-sm">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="mt-0.5 size-4 rounded border-border text-brand-600 focus:ring-brand-600"
              />
              <span className="flex-1">
                <span className="flex items-center gap-1.5 font-medium">
                  <Lock className="size-3.5 text-muted" />
                  Private
                </span>
                <span className="mt-0.5 block text-xs text-muted">
                  Only members you add can see or join. You become the owner.
                </span>
              </span>
            </label>

            {templates.length ? (
              <div className="space-y-1.5">
                <p className="text-sm font-medium">
                  Output templates <span className="font-normal text-muted">(optional)</span>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {templates.map((tpl) => {
                    const on = selected.has(tpl.slug);
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => toggle(tpl.slug)}
                        title={tpl.description}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs transition-colors",
                          on
                            ? "border-brand-600 bg-brand-600/10 text-brand-600"
                            : "border-border text-muted hover:text-text",
                        )}
                      >
                        {tpl.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {error ? <p className="text-sm text-red-500">{error}</p> : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !title.trim()}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              Create topic
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
